import { useEffect, useRef } from 'react';
import {
	getUserWorkspaces,
	getFirstBitbucketWorkspaceId,
} from '../../extensible/products/bitbucket/get-user-workspaces';
import { fg } from '@atlaskit/platform-feature-flags';

export interface BitbucketWorkspacesData {
	bitbucketWorkspacesCount: number | undefined;
	selectedBitbucketWorkspace: string | undefined;
}

/**
 * Fetches Bitbucket workspaces data for analytics purposes.
 * Returns both the total count and the selected (first) workspace UUID.
 * Only fetches if the feature flag is enabled.
 * Returns undefined values if the feature flag is disabled or if an error occurs.
 */
export const fetchBitbucketWorkspacesData = async (): Promise<BitbucketWorkspacesData> => {
	if (!fg('enable_bitbucket_repository_workspace_search_in_qs')) {
		return {
			bitbucketWorkspacesCount: undefined,
			selectedBitbucketWorkspace: undefined,
		};
	}

	try {
		const response = await getUserWorkspaces();
		const workspaceIds = getFirstBitbucketWorkspaceId(response);
		return {
			bitbucketWorkspacesCount: response.size,
			selectedBitbucketWorkspace: workspaceIds[0],
		};
	} catch {
		return {
			bitbucketWorkspacesCount: undefined,
			selectedBitbucketWorkspace: undefined,
		};
	}
};

/**
 * Hook that returns Bitbucket workspaces data for analytics.
 * Fetches on mount and stores the result in a ref.
 */
export const useBitbucketWorkspacesData = (): BitbucketWorkspacesData => {
	const dataRef = useRef<BitbucketWorkspacesData>({
		bitbucketWorkspacesCount: undefined,
		selectedBitbucketWorkspace: undefined,
	});

	useEffect(() => {
		fetchBitbucketWorkspacesData().then((data) => {
			dataRef.current = data;
		});
	}, []);

	return dataRef.current;
};
